import { FormControl, FormControlLabel, Radio, RadioGroup, TextField } from '@mui/material';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InputWrapper } from '@/components/Container/AuthContainer/styles';
import { pblLawFirmPositions } from '@/data/pbl';
import { Box } from '@/layout/Box';
import { Flex } from '@/layout/Flex';

import type { UpdateVolunteerForm, VolunteerFormState } from '../types';
import { FormPanel, FormSectionTitle, SectionTitle, ValidationText } from '../styles';

interface Props {
  form: VolunteerFormState;
  errors: FieldErrors<VolunteerFormState>;
  control: Control<VolunteerFormState>;
  isMobile: boolean;
  isTablet: boolean;
  updateForm: UpdateVolunteerForm;
}

const PracticeInformationStep = ({ form, errors, control, isTablet, updateForm }: Props) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const titleWithNumber = t('pbl.lawyerRegistration.practiceInformation.titleWithNumber', {
    defaultValue: '7. Practice Information',
  });

  return (
    <FormPanel isTablet={isTablet}>
      <SectionTitle>
        {t('pbl.lawyerRegistration.practiceInformation.section', { defaultValue: 'Section 2' })}
      </SectionTitle>
      <Flex gridGap={'5px'}>
        7. <FormSectionTitle>{titleWithNumber.replace(/^7\.\s*/, '')}</FormSectionTitle>
      </Flex>
      <InputWrapper>
        <Controller
          control={control}
          name="law_firm_name"
          rules={{
            required: t('pbl.lawyerRegistration.practiceInformation.errors.lawFirmNameRequired', {
              defaultValue: 'Law firm name is required.',
            }),
          }}
          render={({ field, fieldState }) => (
            <TextField
              label={t('pbl.lawyerRegistration.practiceInformation.fields.lawFirmName', {
                defaultValue: 'Name of Law Firm in which you are Practicing *',
              })}
              variant="standard"
              value={field.value}
              onChange={field.onChange}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              fullWidth
            />
          )}
        />
      </InputWrapper>
      <InputWrapper>
        <Controller
          control={control}
          name="law_firm_name_zh"
          render={({ field, fieldState }) => (
            <TextField
              label={t('pbl.lawyerRegistration.practiceInformation.fields.lawFirmNameZh', {
                defaultValue: 'Chinese Name of Law Firm',
              })}
              variant="standard"
              value={field.value || ''}
              onChange={field.onChange}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              fullWidth
            />
          )}
        />
      </InputWrapper>
      <InputWrapper>
        <Controller
          control={control}
          name="position_in_law_firm"
          rules={{
            required: t('pbl.lawyerRegistration.practiceInformation.errors.positionRequired', {
              defaultValue: 'Position in law firm is required.',
            }),
          }}
          render={({ field, fieldState: { error } }) => (
            <FormControl style={{ marginTop: '20px' }} error={!!error}>
              {t('pbl.lawyerRegistration.practiceInformation.fields.position', {
                defaultValue: '8. Your Position in the Law Firm *',
              })}
              <RadioGroup
                {...field}
                value={field.value || ''}
                onChange={(event) => updateForm('position_in_law_firm', event.target.value)}
              >
                {pblLawFirmPositions.map((position) => (
                  <FormControlLabel
                    key={position.value}
                    value={position.value}
                    control={<Radio color="primary" />}
                    label={t(position.labelKey as any, { defaultValue: position.defaultLabel }) as string}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )}
        />
        {errors.position_in_law_firm?.message && <ValidationText>{errors.position_in_law_firm.message}</ValidationText>}
      </InputWrapper>

      {form.position_in_law_firm === 'other' && (
        <InputWrapper>
          <Flex alignItems="flex-end" width="100%" gridGap={'5px'}>
            <Box paddingBottom={'6px'}>&nbsp;</Box>
            <TextField
              label={t('pbl.lawyerRegistration.practiceInformation.fields.otherPosition', {
                defaultValue: 'Other Position',
              })}
              variant="standard"
              value={form.position_other_text || ''}
              onChange={(event) => updateForm('position_other_text', event.target.value)}
              fullWidth
            />
          </Flex>
        </InputWrapper>
      )}
    </FormPanel>
  );
};

export default PracticeInformationStep;
